// GenericDialog.tsx
import React, { PropsWithChildren, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';

interface GenericDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  headerAction?: React.ReactNode;
  atTop?: boolean;
  errors?: string[];
  onSubmit?: () => void;
  submitLabel?: string;
  showSubmitButton?: boolean;
  disableAutoFocus?: boolean;
  disableEnforceFocus?: boolean;
}

const useStyles = makeStyles<{ atTop?: boolean }>()((theme, { atTop }) => ({
  dialogPaper: {
    borderRadius: theme.shape.borderRadius * 1.6,
    boxShadow: theme.shadows[8] || '0 8px 32px rgba(36, 48, 67, 0.10)',
    background: theme.palette.background.paper,
    maxWidth: 900,
    width: '80vw',
  },
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(3, 4, 0, 4), // Remove bottom padding
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 60%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    position: 'relative',
    borderTopLeftRadius: theme.shape.borderRadius * 1.6,
    borderTopRightRadius: theme.shape.borderRadius * 1.6,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h6.fontSize,
    letterSpacing: 0.5,
    display: 'flex',
    alignItems: 'center',
    minHeight: 64,
  },
  headerAction: {
    position: 'absolute',
    right: theme.spacing(7),
    top: '50%',
    transform: 'translateY(-50%)',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.primary.contrastText,
    background: 'rgba(255,255,255,0.10)',
    borderRadius: '50%',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s, color 0.2s',
    '&:hover, &:focus': {
      background: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
    outline: 'none',
  },
  dialogContent: {
    padding: `${theme.spacing(3, 4, 4, 4)} !important`, // Reduce top padding
    marginTop: 0,
    background: theme.palette.background.default,
    borderBottomLeftRadius: theme.shape.borderRadius * 1.6,
    borderBottomRightRadius: theme.shape.borderRadius * 1.6,
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
  errorAlert: {
    borderRadius: theme.shape.borderRadius * 1.2,
    border: `1px solid ${theme.palette.error.light}33`,
    backgroundColor: theme.palette.error.light || 'rgba(244, 67, 54, 0.05)',
    '& .MuiAlert-icon': {
      color: theme.palette.error.main,
    },
    '& .MuiAlert-message': {
      color: theme.palette.error.dark,
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  errorTitle: {
    color: theme.palette.error.dark,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    marginBottom: theme.spacing(1),
  },
  errorList: {
    margin: 0,
    paddingLeft: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(0.5),
      color: theme.palette.error.dark,
      fontSize: theme.typography.body2.fontSize,
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 4, 3, 4),
    background: theme.palette.background.default,
    borderBottomLeftRadius: theme.shape.borderRadius * 1.6,
    borderBottomRightRadius: theme.shape.borderRadius * 1.6,
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  submitButton: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 3),
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'none',
    fontSize: theme.typography.body1.fontSize,
    boxShadow: theme.shadows[2],
    '&:hover': {
      background: theme.palette.primary.dark,
      boxShadow: theme.shadows[4],
    },
  },
  cancelButton: {
    color: theme.palette.text.secondary,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 3),
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'none',
    fontSize: theme.typography.body1.fontSize,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const GenericDialog: React.FC<PropsWithChildren<GenericDialogProps>> = ({
  open,
  title,
  onClose,
  headerAction,
  atTop = false,
  errors = [],
  onSubmit,
  submitLabel = 'Submit',
  showSubmitButton = false,
  disableAutoFocus = false,
  disableEnforceFocus = false,
  children,
}) => {
  const { classes } = useStyles({ atTop });
  const [dismissedErrors, setDismissedErrors] = useState<Set<number>>(new Set());

  const visibleErrors = errors.filter((_, index) => !dismissedErrors.has(index));

  const handleDismissError = (index: number) => {
    setDismissedErrors(prev => new Set(prev).add(index));
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      disableAutoFocus={disableAutoFocus}
     disableEnforceFocus={disableEnforceFocus}
      classes={{ paper: classes.dialogPaper }}
      PaperProps={{ 
        elevation: 0,
        style: atTop ? { 
          marginTop: '5vh',
          marginBottom: 'auto',
          alignSelf: 'flex-start' 
        } : {}
      }}
      scroll="body"
      sx={atTop ? {
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '5vh',
        },
        '& .MuiBackdrop-root': {
          top: 0,
        }
      } : {}}
    >
      <DialogTitle className={classes.dialogTitle}>
        <span>{title}</span>
        {headerAction && <div className={classes.headerAction}>{headerAction}</div>}
        <IconButton aria-label="Close dialog" data-testid="drawer-close-button" className={classes.closeButton} onClick={onClose} size="large" tabIndex={0}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent} sx={{ paddingTop: 2 }}>
        {visibleErrors.length > 0 && (
          <div className={classes.errorContainer}>
            <Alert 
              severity="error" 
              className={classes.errorAlert}
              onClose={() => {
                const newDismissed = new Set(dismissedErrors);
                errors.forEach((_, index) => newDismissed.add(index));
                setDismissedErrors(newDismissed);
              }}
            >
              <AlertTitle className={classes.errorTitle}>
                {visibleErrors.length === 1 ? 'Error' : 'Errors'}
              </AlertTitle>
              {visibleErrors.length === 1 ? (
                visibleErrors[0]
              ) : (
                <ul className={classes.errorList}>
                  {visibleErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              )}
            </Alert>
          </div>
        )}
        {children}
      </DialogContent>
      {(showSubmitButton || onSubmit) && (
        <DialogActions className={classes.dialogActions}>
          <Button 
            onClick={onClose}
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            className={classes.submitButton}
            variant="contained"
            disableElevation
          >
            {submitLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GenericDialog;